import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AdminManageBuyerBankAccountDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/admin-manage-buyer-bank-account-dto';
import { AdminManageBuyerImplementationFboConfigurationDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/admin-manage-buyer-implementation-fbo-configuration-dto';
import { AdminManageBuyerRevenueShareConfigurationDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/admin-manage-buyer-revenue-share-configuration-dto';
import { CreateOrUpdateBuyerImplementationConfigurationService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/create-or-update-buyer-implementation-configuration';
import {
    GetBuyerImplementationConfigurationResponse,
    GetBuyerImplementationConfigurationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-configuration';
import {
    CreateOrUpdateBuyerConfigurationRequest,
    CreateOrUpdateBuyerConfigurationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/create-or-update-buyer-configuration';
import {
    GetBuyerConfigurationResponse,
    GetBuyerConfigurationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-configuration';
import { Utilities } from '@lsq-frontend/common-ui';
import { CurrencyDashPipe } from '@lsq-frontend/common-ui';
import { LsqValidators, StringI18nValidator } from '@lsq-frontend/common-ui';
import { create, isEmpty } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import {
    ACCOUNT_NUMBER_MAX_LENGTH,
    ACCOUNT_NUMBER_MIN_LENGTH,
    ROUTING_NUMBER_LENGTH
} from '../../../../../constants/bank.constants';
import { ManageBuyerService } from '../manage-buyer.service';
import { AdminManageBuyerInformationChanges } from '../modals/confirm-changes-modal.component';
import { formatBpsInput, formatHbpsInput } from './buyer-configuration-helpers';
import { AdminManageBuyerPayLaterChanges } from './pay-later/buyer-configuration-pay-later.component';

export enum FboAccountType {
    DEFAULT = 'DEFAULT',
    AFX = 'AFX',
    Bank_Of_America = 'Bank_Of_America',
    US_Bank = 'US_Bank',
    Regions_Bank = 'Regions_Bank',
    JP_Morgan = 'JP_Morgan',
    Citizens = 'Citizens'
}

@Injectable()
export class AdminManageBuyerConfigurationService {
    public creditLimitFormGroup = new FormGroup({
        creditLimit: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(1000000000000)
        ]),
        creditLimitCurrency: new FormControl({ disabled: true, value: 'USD' }, [
            Validators.required
        ])
    });
    public defaultFlatFeeFormGroup = new FormGroup({
        defaultFlatFeeRate: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ])
    });
    public maxDaysEarlyFormGroup = new FormGroup({
        maxDaysEarlyEnabled: new FormControl(null, [Validators.required]),
        maxDaysEarlyDays: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(999)
        ])
    });
    public gracePeriodFormGroup = new FormGroup({
        gracePeriodEnabled: new FormControl(null, [Validators.required]),
        gracePeriodDays: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ])
    });
    public pastDueFeeFormGroup = new FormGroup({
        pastDueFeeEnabled: new FormControl(null, [Validators.required]),
        pastDueFeeRate: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ])
    });
    public buyerPaidEarlyPaymentFeesFormGroup = new FormGroup({
        buyerPaidEarlyPaymentFeesEnabled: new FormControl(null, [Validators.required]),
        buyerPaidFeePercentage: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(100)
        ])
    });
    v;
    public buyerPaidEarlyPaymentFeeVisibilityFormGroup = new FormGroup({
        buyerPaidEarlyPaymentFeeVisibilityEnabled: new FormControl(null, [Validators.required]),
        buyerPaidEarlyPaymentFeeVisibilityActive: new FormControl(false)
    });

    public buyerPaidEarlyPaymentTransferFeesFormGroup = new FormGroup({
        buyerPaidEarlyPaymentTransferFeesEnabled: new FormControl(null, [Validators.required]),
        buyerPaidEarlyPaymentTransferFeesActive: new FormControl(false)
    });
    public decayingFeeFormGroup = new FormGroup({
        decayingFeeRate: new FormControl(null, [Validators.required])
    });
    public ineligibleDaysBeforeTermsFormGroup = new FormGroup({
        ineligibleDaysBeforeTermsEnabled: new FormControl(null),
        ineligibleDaysBeforeTerms: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ])
    });

    public adjustable120DayFormGroup = new FormGroup({
        adjustable120DayEnabled: new FormControl(null),
        adjustable120DayActive: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(365)
        ])
    });

    public revenueShareFormGroup: FormGroup = new FormGroup({
        enableRevenueShare: new FormControl(null, [Validators.required]),
        platformRevenueShare: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),
        lsqRevenueShare: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),
        primaryBuyerRevenueShare: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),
        secondaryBuyerRevenueShare: new FormControl(null, [
            Validators.required,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),
        primaryDueDateThreshold: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),
        secondaryDueDateThreshold: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(10000)
        ]),

        bankAccountName: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        bankAccountNumber: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.minLength(ACCOUNT_NUMBER_MIN_LENGTH),
            this._stringI18nValidator.maxLength(ACCOUNT_NUMBER_MAX_LENGTH),
            this._stringI18nValidator.matchValueSource(
                () => this.revenueShareFormGroup?.get('verifyBankAccountNumber'),
                'Account numbers do not match.'
            )
        ]),
        verifyBankAccountNumber: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.matchValueConfirm(
                () => this.revenueShareFormGroup?.get('bankAccountNumber'),
                'Account numbers do not match.'
            )
        ]),
        routingNumber: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)
        ]),
        bankName: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)])
    });
    public tpmFormGroup: FormGroup = new FormGroup({
        tpmEnabled: new FormControl(null),
        companyId: new FormControl(null),
        senderId: new FormControl(null),
        fboAccountType: new FormControl(FboAccountType.Bank_Of_America, [Validators.required]),
        outputFolderId: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.maxLength(255)
        ]),
        bankAccountName: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        bankAccountNumber: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.minLength(ACCOUNT_NUMBER_MIN_LENGTH),
            this._stringI18nValidator.maxLength(ACCOUNT_NUMBER_MAX_LENGTH),
            this._stringI18nValidator.matchValueSource(
                () => this.revenueShareFormGroup?.get('verifyBankAccountNumber'),
                'Account numbers do not match.'
            )
        ]),
        bankName: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        verifyBankAccountNumber: new FormControl(null, [
            Validators.required,
            this._stringI18nValidator.matchValueConfirm(
                () => this.revenueShareFormGroup?.get('bankAccountNumber'),
                'Account numbers do not match.'
            )
        ]),
        achRoutingNumber: new FormControl(null, [
            this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)
        ]),
        wireRoutingNumber: new FormControl(null, [
            this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)
        ])
    });
    public payLaterFormGroup: FormGroup = new FormGroup({
        payLaterEnabled: new FormControl(null),
        facilityLimit: new FormControl(null),
        facilityLimitCurrencyCode: new FormControl('USD'),
        daysIncrement: new FormControl(60),
        feeDailyPercent: new FormControl(null),
        lateFeeDailyPercent: new FormControl(null),
        maxExtendableInvoiceAgeDays: new FormControl(60)
    });

    public ingestAllInvoicesFormGroup: FormGroup = new FormGroup({
        ingestAllInvoicesEnabled: new FormControl(null, [Validators.required])
    });

    public inforNexusEnabledFormGroup: FormGroup = new FormGroup({
        inforNexusEnabled: new FormControl(null, [Validators.required])
    });

    private _formsInitializedSubject = new BehaviorSubject<boolean>(false);
    public formsInitialized$ = this._formsInitializedSubject.asObservable();

    private _revenueShareBankAccountSubject = new BehaviorSubject<{
        bankAccountName: string;
        bankAccountBankName: string;
        bankAccountNumberLastFour: string;
        bankAccountRoutingNumberLastFour: string;
    }>(null);
    public revenueShareBankAccount$ = this._revenueShareBankAccountSubject.asObservable();

    private _isUpdatingRevenueShareBankAccountSubject = new BehaviorSubject<boolean>(false);
    public isUpdatingRevenueShareBankAccount$ =
        this._isUpdatingRevenueShareBankAccountSubject.asObservable();

    private _tpmBankAccountSubject = new BehaviorSubject<{
        bankAccountName: string;
        bankAccountBankName: string;
        accountNumberLastFour: string;
        achRoutingNumberLastFour: string;
        wireRoutingNumberLastFour: string;
    }>(null);
    public tpmBankAccount$ = this._tpmBankAccountSubject.asObservable();

    private _isUpdatingTpmBankAccountSubject = new BehaviorSubject<boolean>(false);
    public isUpdatingTpmBankAccount$ = this._isUpdatingTpmBankAccountSubject.asObservable();

    private _cachedBuyerKey: string;
    private _cachedBuyerConfigurationResponse:
        | GetBuyerImplementationConfigurationResponse
        | GetBuyerConfigurationResponse;

    private _conditionalRequiredRevenueShareFieldsSubscription: Subscription;
    private _conditionalTpmBankAccountFieldsSubscription: Subscription;
    private _conditionalTpmAchFieldsSubscription: Subscription;
    private _conditionalDecayingFeeSubscription: Subscription;
    private _conditionalDefaultFeeSubscription: Subscription;

    public creditLimitChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public revenueShareConfigurationChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public tpmConfigurationChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public defaultFlatFeeChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public maxDaysEarlyChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public gracePeriodChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public ingestAllInvoicesChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public inforNexusEnabledChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public pastDueFeeChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public buyerPaidEarlyPaymentFeesChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public buyerPaidEarlyPaymentFeesVisibilityChanges$: Observable<
        AdminManageBuyerInformationChanges[]
    >;
    public buyerPaidEarlyPaymentTransferFeesChanges$: Observable<
        AdminManageBuyerInformationChanges[]
    >;
    public decayingFeeChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public ineligibleDaysBeforeTermsChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public payLaterChanges$: Observable<AdminManageBuyerPayLaterChanges[]>;
    public adjustable120DaysChanges$: Observable<AdminManageBuyerInformationChanges[]>;
    public hasChanges$: Observable<boolean>;

    constructor(
        private _stringI18nValidator: StringI18nValidator,
        private _manageBuyerService: ManageBuyerService,
        private _getBuyerConfigurationService: GetBuyerConfigurationService,
        private _getBuyerImplementationConfigurationService: GetBuyerImplementationConfigurationService,
        private _createOrUpdateBuyerConfigurationService: CreateOrUpdateBuyerConfigurationService,
        private _createOrUpdateBuyerImplementationConfigurationService: CreateOrUpdateBuyerImplementationConfigurationService,

        private _currencyPipe: CurrencyDashPipe
    ) {}

    public async initializeFormGroups(buyerKey: string): Promise<void> {
        this._cachedBuyerKey = buyerKey;
        this._isUpdatingRevenueShareBankAccountSubject.next(false);
        this._formsInitializedSubject.next(false);
        this._toggleRevenueShareBankAccountValidators(false);

        if (this._conditionalRequiredRevenueShareFieldsSubscription) {
            this._conditionalRequiredRevenueShareFieldsSubscription.unsubscribe();
        }

        if (this._conditionalTpmBankAccountFieldsSubscription) {
            this._conditionalTpmBankAccountFieldsSubscription.unsubscribe();
        }
        if (this._conditionalTpmAchFieldsSubscription) {
            this._conditionalTpmAchFieldsSubscription.unsubscribe();
        }

        if (this._conditionalDecayingFeeSubscription) {
            this._conditionalDecayingFeeSubscription.unsubscribe();
        }

        if (this._conditionalDefaultFeeSubscription) {
            this._conditionalDefaultFeeSubscription.unsubscribe();
        }

        const isImplementation = await this._manageBuyerService.isImplementation$
            .pipe(first())
            .toPromise();

        if (isImplementation) {
            this._cachedBuyerConfigurationResponse =
                await this._getBuyerImplementationConfigurationService.executeOnce({
                    buyerImplementationKey: buyerKey
                });
        } else {
            this._cachedBuyerConfigurationResponse =
                await this._getBuyerConfigurationService.executeOnce({ buyerKey });
        }

        this.resetForms();

        // The revenue share bank account fields are optional during implementation,
        // but if any of the fields are filled out, then they should all be required.
        this._conditionalRequiredRevenueShareFieldsSubscription = combineLatest([
            this._manageBuyerService.isImplementation$,
            this.isUpdatingRevenueShareBankAccount$,
            this.revenueShareFormGroup.valueChanges
        ]).subscribe(([isImplementation, isUpdatingRevenueShareBankAccount, formValues]) => {
            const bankAccountFormFields = {
                bankAccountName: formValues.bankAccountName,
                bankName: formValues.bankName,
                bankAccountNumber: formValues.bankAccountNumber,
                verifyBankAccountNumber: formValues.verifyBankAccountNumber,
                routingNumber: formValues.routingNumber
            };

            const bankFormHasChanges = !Object.values(bankAccountFormFields).every(
                (x) => x === null || x === ''
            );

            if (!isUpdatingRevenueShareBankAccount) {
                this._toggleRevenueShareBankAccountValidators(false);
            } else if (isUpdatingRevenueShareBankAccount && !isImplementation) {
                this._toggleRevenueShareBankAccountValidators(true);
            } else if (isUpdatingRevenueShareBankAccount && isImplementation) {
                this._toggleRevenueShareBankAccountValidators(false);
            }

            if (bankFormHasChanges) {
                this._toggleRevenueShareBankAccountValidators(true);
            }
        });

        this._conditionalTpmBankAccountFieldsSubscription =
            this.isUpdatingTpmBankAccount$.subscribe((isUpdating) => {
                this._toggleTpmBankAccountValidators(isUpdating);
            });

        this._conditionalTpmAchFieldsSubscription = this.tpmFormGroup
            .get('achRoutingNumber')
            .valueChanges.subscribe((value) => {
                const companyIdFormControl = this.tpmFormGroup.get('companyId');
                const senderIdFormControl = this.tpmFormGroup.get('senderId');

                if (
                    value !== null &&
                    value !== '' &&
                    this.tpmFormGroup.get('achRoutingNumber').enabled
                ) {
                    companyIdFormControl.setValidators([Validators.required]);
                    senderIdFormControl.setValidators([Validators.required]);
                } else {
                    companyIdFormControl.setValidators([]);
                    senderIdFormControl.setValidators([]);
                }
                companyIdFormControl.updateValueAndValidity({ emitEvent: false });
                senderIdFormControl.updateValueAndValidity({ emitEvent: false });
            });

        this._conditionalDecayingFeeSubscription = this.decayingFeeFormGroup
            .get('decayingFeeRate')
            .valueChanges.subscribe((value) => {
                const decayingFeeRate = this.decayingFeeFormGroup.get('decayingFeeRate');
                const defaultFeeRate = this.defaultFlatFeeFormGroup.get('defaultFlatFeeRate');

                if (value !== null && value !== '') {
                    defaultFeeRate.setValidators([
                        LsqValidators.minNumber(0),
                        LsqValidators.maxNumber(10000)
                    ]);
                } else if (defaultFeeRate.value !== null && defaultFeeRate.value !== '') {
                    decayingFeeRate.setValidators([]);
                    defaultFeeRate.setValidators([
                        Validators.required,
                        LsqValidators.minNumber(0),
                        LsqValidators.maxNumber(10000)
                    ]);
                } else {
                    decayingFeeRate.setValidators([Validators.required]);
                    defaultFeeRate.setValidators([
                        Validators.required,
                        LsqValidators.minNumber(0),
                        LsqValidators.maxNumber(10000)
                    ]);
                }

                decayingFeeRate.updateValueAndValidity({ emitEvent: false });
                defaultFeeRate.updateValueAndValidity({ emitEvent: false });
            });

        this._conditionalDefaultFeeSubscription = this.defaultFlatFeeFormGroup
            .get('defaultFlatFeeRate')
            .valueChanges.subscribe((value) => {
                const decayingFeeRate = this.decayingFeeFormGroup.get('decayingFeeRate');
                const defaultFeeRate = this.defaultFlatFeeFormGroup.get('defaultFlatFeeRate');

                if (value !== null && value !== '') {
                    decayingFeeRate.setValidators([]);
                } else if (decayingFeeRate.value !== null && decayingFeeRate.value !== '') {
                    decayingFeeRate.setValidators([Validators.required]);
                    defaultFeeRate.setValidators([
                        LsqValidators.minNumber(0),
                        LsqValidators.maxNumber(10000)
                    ]);
                } else {
                    decayingFeeRate.setValidators([Validators.required]);
                    defaultFeeRate.setValidators([
                        Validators.required,
                        LsqValidators.minNumber(0),
                        LsqValidators.maxNumber(10000)
                    ]);
                }

                decayingFeeRate.updateValueAndValidity({ emitEvent: false });
                defaultFeeRate.updateValueAndValidity({ emitEvent: false });
            });

        this.decayingFeeFormGroup
            .get('decayingFeeRate')
            .updateValueAndValidity({ emitEvent: true });

        this._manageBuyerService.isImplementation$.subscribe((isImplementation) => {
            this.creditLimitFormGroup
                .get('creditLimit')
                .setValidators(
                    isImplementation
                        ? [LsqValidators.minNumber(0), LsqValidators.maxNumber(1000000000000)]
                        : [
                              Validators.required,
                              LsqValidators.minNumber(0),
                              LsqValidators.maxNumber(1000000000000)
                          ]
                );
            this.creditLimitFormGroup
                .get('creditLimit')
                .updateValueAndValidity({ emitEvent: false });
        });

        this._formsInitializedSubject.next(true);

        Utilities.executeAfterRender(() => {
            this._togglePayLaterSection(false);
            this._setPayLaterValidators();
        });
    }

    public resetForms(): void {
        this._setupChangesStreams();

        this.creditLimitFormGroup.reset({
            creditLimit:
                this._cachedBuyerConfigurationResponse.buyerCreditLimitCents !== null
                    ? (this._cachedBuyerConfigurationResponse.buyerCreditLimitCents / 100).toFixed(
                          2
                      )
                    : null,
            creditLimitCurrency: 'USD'
        });

        this.defaultFlatFeeFormGroup.reset({
            defaultFlatFeeRate: formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerDefaultFlatFeeRatePercent
            )
        });

        this.maxDaysEarlyFormGroup.reset({
            maxDaysEarlyEnabled:
                this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly !== null,
            maxDaysEarlyDays:
                this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly
        });

        this.gracePeriodFormGroup.reset({
            gracePeriodEnabled:
                this._cachedBuyerConfigurationResponse.buyerGracePeriodDays !== null,
            gracePeriodDays: this._cachedBuyerConfigurationResponse.buyerGracePeriodDays
        });

        this.ingestAllInvoicesFormGroup.reset({
            ingestAllInvoicesEnabled:
                this._cachedBuyerConfigurationResponse.buyerIngestAllInvoices !== null,
            ingestAllInvoices: this._cachedBuyerConfigurationResponse.buyerIngestAllInvoices
        });

        this.inforNexusEnabledFormGroup.reset({
            inforNexusEnabled:
                this._cachedBuyerConfigurationResponse.buyerInforNexusEnabled !== null
        });

        this.pastDueFeeFormGroup.reset({
            pastDueFeeEnabled:
                this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent !== null,
            pastDueFeeRate: formatHbpsInput(
                this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent
            )
        });

        this.buyerPaidEarlyPaymentFeesFormGroup.reset({
            buyerPaidEarlyPaymentFeesEnabled:
                this._cachedBuyerConfigurationResponse.buyerPaidFeePercentage !== null,
            buyerPaidFeePercentage: this._cachedBuyerConfigurationResponse.buyerPaidFeePercentage
        });

        this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.reset({
            buyerPaidEarlyPaymentFeeVisibilityEnabled:
                this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible !== null ||
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible !== null,
            buyerPaidEarlyPaymentFeeVisibilityActive:
                (this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible ?? false) &&
                (this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible ?? false)
        });

        this.buyerPaidEarlyPaymentTransferFeesFormGroup.reset({
            buyerPaidEarlyPaymentTransferFeesEnabled:
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee !== null,
            buyerPaidEarlyPaymentTransferFeesActive:
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee ?? false
        });

        this.decayingFeeFormGroup.reset({
            decayingFeeRate: formatHbpsInput(
                this._cachedBuyerConfigurationResponse.buyerDecayingFeeRatePercent
            )
        });

        this.ineligibleDaysBeforeTermsFormGroup.reset({
            ineligibleDaysBeforeTermsEnabled:
                this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms !== null,
            ineligibleDaysBeforeTerms:
                this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms
        });
        this.adjustable120DayFormGroup.reset({
            adjustable120DayEnabled:
                this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays !== null,
            adjustable120DayActive:
                this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays
        });

        this.revenueShareFormGroup.reset({
            enableRevenueShare:
                this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent !== null,
            platformRevenueShare: formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent
            ),
            lsqRevenueShare: formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerLsqRevenueSharePercent
            ),
            primaryBuyerRevenueShare: formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerPrimaryBuyerRevenueSharePercent
            ),
            secondaryBuyerRevenueShare: formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerSecondaryBuyerRevenueSharePercent
            ),
            primaryDueDateThreshold:
                this._cachedBuyerConfigurationResponse.buyerPrimaryDueDateThreshold,
            secondaryDueDateThreshold:
                this._cachedBuyerConfigurationResponse.buyerSecondaryDueDateThreshold,

            bankAccountName: null,
            bankAccountNumber: null,
            verifyBankAccountNumber: null,
            routingNumber: null,
            bankName: null
        });

        if (
            this._cachedBuyerConfigurationResponse.bankAccountNumberLastFour &&
            this._cachedBuyerConfigurationResponse.bankAccountRoutingNumberLastFour &&
            this._cachedBuyerConfigurationResponse.bankAccountName &&
            this._cachedBuyerConfigurationResponse.bankAccountBankName
        ) {
            this._revenueShareBankAccountSubject.next({
                bankAccountNumberLastFour:
                    this._cachedBuyerConfigurationResponse.bankAccountNumberLastFour,
                bankAccountRoutingNumberLastFour:
                    this._cachedBuyerConfigurationResponse.bankAccountRoutingNumberLastFour,
                bankAccountName: this._cachedBuyerConfigurationResponse.bankAccountName,
                bankAccountBankName: this._cachedBuyerConfigurationResponse.bankAccountBankName
            });
            this.setIsUpdatingRevenueShareBankAccount(false);
        } else {
            this._revenueShareBankAccountSubject.next(null);
            this.setIsUpdatingRevenueShareBankAccount(true);
        }

        this.tpmFormGroup.reset({
            tpmEnabled: !!this._cachedBuyerConfigurationResponse.fboConfiguration,
            companyId: this._cachedBuyerConfigurationResponse.fboConfiguration?.bankCompanyId,
            senderId: this._cachedBuyerConfigurationResponse.fboConfiguration?.bankSenderId,
            outputFolderId: this._cachedBuyerConfigurationResponse.fboConfiguration?.outputFolderId,
            fboAccountType:
                this._cachedBuyerConfigurationResponse.fboConfiguration?.fboAccountType ||
                FboAccountType.Bank_Of_America
        });

        if (!!this._cachedBuyerConfigurationResponse.fboConfiguration?.accountName) {
            this._tpmBankAccountSubject.next({
                bankAccountName:
                    this._cachedBuyerConfigurationResponse.fboConfiguration.accountName,
                bankAccountBankName:
                    this._cachedBuyerConfigurationResponse.fboConfiguration.bankName,
                accountNumberLastFour:
                    this._cachedBuyerConfigurationResponse.fboConfiguration.accountNumberLastFour,
                achRoutingNumberLastFour:
                    this._cachedBuyerConfigurationResponse.fboConfiguration
                        .achRoutingNumberLastFour,
                wireRoutingNumberLastFour:
                    this._cachedBuyerConfigurationResponse.fboConfiguration
                        .wireRoutingNumberLastFour
            });

            this.setIsUpdatingTpmBankAccount(false);
        } else {
            this._tpmBankAccountSubject.next(null);
            this.setIsUpdatingTpmBankAccount(true);
        }

        this._toggleMaxDaysPaidEarly();
        this._toggleIneligibleDayTerms();
        this._toggleAdjustable120DaysRuleSection();

        const payLaterConfiguration = (
            this._cachedBuyerConfigurationResponse as GetBuyerConfigurationResponse
        ).payLaterConfiguration;

        this.payLaterFormGroup.reset({
            payLaterEnabled: payLaterConfiguration?.eligible,
            facilityLimit: payLaterConfiguration?.facilityLimitCents
                ? (payLaterConfiguration.facilityLimitCents / 100).toFixed(2)
                : null,
            facilityLimitCurrencyCode: payLaterConfiguration?.facilityLimitCurrencyCode || 'USD',
            daysIncrement: payLaterConfiguration?.daysIncrement
                ? payLaterConfiguration.daysIncrement
                : 60,
            feeDailyPercent: payLaterConfiguration?.feeDailyPercent
                ? payLaterConfiguration.feeDailyPercent.toFixed(4)
                : null,
            lateFeeDailyPercent: payLaterConfiguration?.lateFeeDailyPercent
                ? payLaterConfiguration.lateFeeDailyPercent.toFixed(4)
                : null,
            maxExtendableInvoiceAgeDays: payLaterConfiguration?.maxExtendableInvoiceAgeDays
                ? payLaterConfiguration.maxExtendableInvoiceAgeDays
                : null
        });
        this._togglePayLaterSection();
    }

    public setIsUpdatingRevenueShareBankAccount(isUpdating: boolean): void {
        this._isUpdatingRevenueShareBankAccountSubject.next(isUpdating);
    }

    public setIsUpdatingTpmBankAccount(isUpdating: boolean): void {
        this._isUpdatingTpmBankAccountSubject.next(isUpdating);
    }

    public destroyForms(): void {
        this.creditLimitFormGroup.reset({});
        this.defaultFlatFeeFormGroup.reset({});
        this.maxDaysEarlyFormGroup.reset({});
        this.gracePeriodFormGroup.reset({});
        this.ingestAllInvoicesFormGroup.reset({});
        this.inforNexusEnabledFormGroup.reset({});
        this.pastDueFeeFormGroup.reset({});
        this.decayingFeeFormGroup.reset({});
        this.ineligibleDaysBeforeTermsFormGroup.reset({});
        this.adjustable120DayFormGroup.reset({});
        this.revenueShareFormGroup.reset({});
        this.tpmFormGroup.reset({});
        this.buyerPaidEarlyPaymentFeesFormGroup.reset({});
        this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.reset({});
        this.buyerPaidEarlyPaymentTransferFeesFormGroup.reset({});
        this.payLaterFormGroup.reset({});

        this.creditLimitChanges$ = null;
        this.revenueShareConfigurationChanges$ = null;
        this.defaultFlatFeeChanges$ = null;
        this.maxDaysEarlyChanges$ = null;
        this.gracePeriodChanges$ = null;
        this.ingestAllInvoicesChanges$ = null;
        this.inforNexusEnabledChanges$ = null;
        this.pastDueFeeChanges$ = null;
        this.buyerPaidEarlyPaymentFeesChanges$ = null;
        this.buyerPaidEarlyPaymentFeesVisibilityChanges$ = null;
        this.buyerPaidEarlyPaymentTransferFeesChanges$ = null;
        this.decayingFeeChanges$ = null;
        this.ineligibleDaysBeforeTermsChanges$ = null;
        this.adjustable120DaysChanges$ = null;
        this.hasChanges$ = null;
        this.payLaterChanges$ = null;

        this._revenueShareBankAccountSubject.next(null);
        this._isUpdatingRevenueShareBankAccountSubject.next(false);
        this._isUpdatingTpmBankAccountSubject.next(false);
        this._formsInitializedSubject.next(false);

        if (this._conditionalRequiredRevenueShareFieldsSubscription) {
            this._conditionalRequiredRevenueShareFieldsSubscription.unsubscribe();
        }

        if (this._conditionalTpmBankAccountFieldsSubscription) {
            this._conditionalTpmBankAccountFieldsSubscription.unsubscribe();
        }
        if (this._conditionalTpmAchFieldsSubscription) {
            this._conditionalTpmAchFieldsSubscription.unsubscribe();
        }

        if (this._conditionalDefaultFeeSubscription) {
            this._conditionalDefaultFeeSubscription.unsubscribe();
        }

        if (this._conditionalDecayingFeeSubscription) {
            this._conditionalDecayingFeeSubscription.unsubscribe();
        }
    }

    public async commitChanges(): Promise<void> {
        const isImplementation = await this._manageBuyerService.isImplementation$
            .pipe(first())
            .toPromise();

        const createOrUpdateBuyerConfigurationRequest =
            new CreateOrUpdateBuyerConfigurationRequest();
        createOrUpdateBuyerConfigurationRequest.buyerKey = this._cachedBuyerKey;

        if (this.creditLimitFormGroup.value.creditLimit !== null) {
            createOrUpdateBuyerConfigurationRequest.creditLimitCents =
                this.creditLimitFormGroup.value.creditLimit * 100;

            // Force USD for now.
            createOrUpdateBuyerConfigurationRequest.creditLimitCurrencyCode = 'USD';
        }

        createOrUpdateBuyerConfigurationRequest.defaultFlatFeeRatePercent =
            this.defaultFlatFeeFormGroup.value.defaultFlatFeeRate;

        if (this.maxDaysEarlyFormGroup.value.maxDaysEarlyEnabled) {
            createOrUpdateBuyerConfigurationRequest.invoiceMaximumDaysPaidEarly =
                this.maxDaysEarlyFormGroup.value.maxDaysEarlyDays;
        }

        if (this.gracePeriodFormGroup.value.gracePeriodEnabled) {
            createOrUpdateBuyerConfigurationRequest.gracePeriodDays =
                this.gracePeriodFormGroup.value.gracePeriodDays;
        }
        if (this.ingestAllInvoicesFormGroup.value.ingestAllInvoicesEnabled) {
            createOrUpdateBuyerConfigurationRequest.ingestAllInvoices = true;
        }

        if (this.inforNexusEnabledFormGroup.value.inforNexusEnabled) {
            createOrUpdateBuyerConfigurationRequest.inforNexusEnabled = true;
        }

        if (this.pastDueFeeFormGroup.value.pastDueFeeEnabled) {
            createOrUpdateBuyerConfigurationRequest.pastDueFeePercent =
                this.pastDueFeeFormGroup.value.pastDueFeeRate;
        }

        if (this.buyerPaidEarlyPaymentFeesFormGroup.value.buyerPaidEarlyPaymentFeesEnabled) {
            createOrUpdateBuyerConfigurationRequest.buyerPaidFeePercentage =
                this.buyerPaidEarlyPaymentFeesFormGroup.value.buyerPaidFeePercentage;
        }

        if (
            this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.value
                .buyerPaidEarlyPaymentFeeVisibilityEnabled
        ) {
            createOrUpdateBuyerConfigurationRequest.buyerPaidFeeVisible =
                this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.value
                    .buyerPaidEarlyPaymentFeeVisibilityActive === null
                    ? false
                    : this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.value
                          .buyerPaidEarlyPaymentFeeVisibilityActive;
        }

        if (
            this.buyerPaidEarlyPaymentTransferFeesFormGroup.value
                .buyerPaidEarlyPaymentTransferFeesEnabled
        ) {
            createOrUpdateBuyerConfigurationRequest.buyerPaidTransactionFee =
                this.buyerPaidEarlyPaymentTransferFeesFormGroup.value
                    .buyerPaidEarlyPaymentTransferFeesActive === null
                    ? false
                    : this.buyerPaidEarlyPaymentTransferFeesFormGroup.value
                          .buyerPaidEarlyPaymentTransferFeesActive;
        }

        createOrUpdateBuyerConfigurationRequest.decayingFeeRatePercent =
            this.decayingFeeFormGroup.value.decayingFeeRate;

        if (this.ineligibleDaysBeforeTermsFormGroup.value.ineligibleDaysBeforeTermsEnabled) {
            createOrUpdateBuyerConfigurationRequest.ineligibleDaysBeforeTerms =
                this.ineligibleDaysBeforeTermsFormGroup.value.ineligibleDaysBeforeTerms;
        }

        if (this.adjustable120DayFormGroup.value.adjustable120DayEnabled) {
            createOrUpdateBuyerConfigurationRequest.adjustable120DayRule =
                this.adjustable120DayFormGroup.value.adjustable120DayActive;
        }

        if (this.revenueShareFormGroup.value.enableRevenueShare) {
            const revenueShareConfigurationDto: AdminManageBuyerRevenueShareConfigurationDto = {
                platformRevenueSharePercent: this.revenueShareFormGroup.value.platformRevenueShare,
                lsqRevenueSharePercent: this.revenueShareFormGroup.value.lsqRevenueShare,
                primaryBuyerRevenueSharePercent:
                    this.revenueShareFormGroup.value.primaryBuyerRevenueShare,
                secondaryBuyerRevenueSharePercent:
                    this.revenueShareFormGroup.value.secondaryBuyerRevenueShare,
                primaryDueDateThreshold: this.revenueShareFormGroup.value.primaryDueDateThreshold,
                secondaryDueDateThreshold:
                    this.revenueShareFormGroup.value.secondaryDueDateThreshold
            };

            if (
                (await this.isUpdatingRevenueShareBankAccount$.pipe(first()).toPromise()) &&
                this.revenueShareFormGroup.value.bankAccountName &&
                this.revenueShareFormGroup.value.bankAccountNumber &&
                this.revenueShareFormGroup.value.routingNumber &&
                this.revenueShareFormGroup.value.bankName
            ) {
                const revenueShareBankAccountDto: AdminManageBuyerBankAccountDto = {
                    bankAccountName: this.revenueShareFormGroup.value.bankAccountName,
                    bankAccountNumber: this.revenueShareFormGroup.value.bankAccountNumber,
                    bankAccountRoutingNumber: this.revenueShareFormGroup.value.routingNumber,
                    bankAccountBankName: this.revenueShareFormGroup.value.bankName
                };
                revenueShareConfigurationDto.bankAccount = revenueShareBankAccountDto;
            }

            createOrUpdateBuyerConfigurationRequest.revenueShareConfiguration =
                revenueShareConfigurationDto;
        }

        if (this.tpmFormGroup.value.tpmEnabled) {
            const fboConfiguration: AdminManageBuyerImplementationFboConfigurationDto = {
                accountName: this.tpmFormGroup.value.bankAccountName,
                bankName: this.tpmFormGroup.value.bankName,
                accountNumber: this.tpmFormGroup.value.bankAccountNumber,
                // The backend will choke getting the last 4 digits if it gets an empty string
                achRoutingNumber: this.tpmFormGroup.value.achRoutingNumber
                    ? this.tpmFormGroup.value.achRoutingNumber
                    : null,
                // The backend will choke getting the last 4 digits if it gets an empty string
                wireRoutingNumber: this.tpmFormGroup.value.wireRoutingNumber
                    ? this.tpmFormGroup.value.wireRoutingNumber
                    : null,
                bankCompanyId: this.tpmFormGroup.value.companyId,
                bankSenderId: this.tpmFormGroup.value.senderId,
                fboAccountType: this.tpmFormGroup.value.fboAccountType,
                outputFolderId: this.tpmFormGroup.value.outputFolderId
            };
            createOrUpdateBuyerConfigurationRequest.fboConfiguration = fboConfiguration;

            if (this.payLaterFormGroup.value.payLaterEnabled) {
                createOrUpdateBuyerConfigurationRequest.payLaterConfiguration = {
                    eligible: true,
                    facilityLimitCents: this.payLaterFormGroup.value.facilityLimit * 100,
                    facilityLimitCurrencyCode:
                        this.payLaterFormGroup.value.facilityLimitCurrencyCode,
                    feeDailyPercent: +this.payLaterFormGroup.value.feeDailyPercent,
                    lateFeeDailyPercent: +this.payLaterFormGroup.value.lateFeeDailyPercent,
                    daysIncrement: +this.payLaterFormGroup.value.daysIncrement,
                    maxExtendableInvoiceAgeDays:
                        +this.payLaterFormGroup.value.maxExtendableInvoiceAgeDays
                };
            }
        }

        if (!isImplementation) {
            await this._createOrUpdateBuyerConfigurationService.executeOnce(
                createOrUpdateBuyerConfigurationRequest
            );
        } else {
            const { buyerKey, ...request } = createOrUpdateBuyerConfigurationRequest;
            await this._createOrUpdateBuyerImplementationConfigurationService.executeOnce({
                buyerImplementationKey: this._cachedBuyerKey,
                ...request
            });
        }
    }

    private _toggleRevenueShareBankAccountValidators(enable: boolean): void {
        if (enable) {
            this.revenueShareFormGroup
                .get('bankAccountName')
                .setValidators([Validators.required, LsqValidators.maxLength(255)]);
            this.revenueShareFormGroup
                .get('bankAccountNumber')
                .setValidators([
                    Validators.required,
                    this._stringI18nValidator.minLength(ACCOUNT_NUMBER_MIN_LENGTH),
                    this._stringI18nValidator.maxLength(ACCOUNT_NUMBER_MAX_LENGTH),
                    this._stringI18nValidator.matchValueSource(
                        () => this.revenueShareFormGroup?.get('verifyBankAccountNumber'),
                        'Account numbers do not match.'
                    )
                ]);
            this.revenueShareFormGroup
                .get('verifyBankAccountNumber')
                .setValidators([
                    Validators.required,
                    this._stringI18nValidator.matchValueConfirm(
                        () => this.revenueShareFormGroup?.get('bankAccountNumber'),
                        'Account numbers do not match.'
                    )
                ]);
            this.revenueShareFormGroup
                .get('routingNumber')
                .setValidators([
                    Validators.required,
                    this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)
                ]);
            this.revenueShareFormGroup
                .get('bankName')
                .setValidators([Validators.required, LsqValidators.maxLength(255)]);
        } else {
            this.revenueShareFormGroup.get('bankAccountName').setValidators([]);
            this.revenueShareFormGroup.get('bankAccountNumber').setValidators([]);
            this.revenueShareFormGroup.get('verifyBankAccountNumber').setValidators([]);
            this.revenueShareFormGroup.get('routingNumber').setValidators([]);
            this.revenueShareFormGroup.get('bankName').setValidators([]);
        }

        this.revenueShareFormGroup
            .get('bankAccountName')
            .updateValueAndValidity({ emitEvent: false });
        this.revenueShareFormGroup
            .get('bankAccountNumber')
            .updateValueAndValidity({ emitEvent: false });
        this.revenueShareFormGroup
            .get('verifyBankAccountNumber')
            .updateValueAndValidity({ emitEvent: false });
        this.revenueShareFormGroup
            .get('routingNumber')
            .updateValueAndValidity({ emitEvent: false });
        this.revenueShareFormGroup.get('bankName').updateValueAndValidity({ emitEvent: false });
    }

    private _toggleAdjustable120DaysRuleSection() {
        if (this.adjustable120DayFormGroup.get('adjustable120DayEnabled').value) {
            this.adjustable120DayFormGroup
                .get('adjustable120DayActive')
                .enable({ emitEvent: false });
        } else {
            this.adjustable120DayFormGroup
                .get('adjustable120DayActive')
                .disable({ emitEvent: false });
        }
    }

    private _toggleMaxDaysPaidEarly() {
        if (this.maxDaysEarlyFormGroup.get('maxDaysEarlyEnabled').value) {
            this.maxDaysEarlyFormGroup.get('maxDaysEarlyDays').enable({ emitEvent: false });
        } else {
            this.maxDaysEarlyFormGroup.get('maxDaysEarlyDays').disable({ emitEvent: false });
        }
    }

    private _toggleIneligibleDayTerms() {
        if (this.ineligibleDaysBeforeTermsFormGroup.get('ineligibleDaysBeforeTermsEnabled').value) {
            this.ineligibleDaysBeforeTermsFormGroup
                .get('ineligibleDaysBeforeTerms')
                .enable({ emitEvent: false });
        } else {
            this.ineligibleDaysBeforeTermsFormGroup
                .get('ineligibleDaysBeforeTerms')
                .disable({ emitEvent: false });
        }
    }

    private _toggleTpmBankAccountValidators(enable: boolean): void {
        if (enable) {
            this.tpmFormGroup
                .get('bankAccountName')
                .setValidators([Validators.required, LsqValidators.maxLength(255)]);
            this.tpmFormGroup
                .get('bankAccountNumber')
                .setValidators([
                    Validators.required,
                    this._stringI18nValidator.minLength(ACCOUNT_NUMBER_MIN_LENGTH),
                    this._stringI18nValidator.maxLength(ACCOUNT_NUMBER_MAX_LENGTH),
                    this._stringI18nValidator.matchValueSource(
                        () => this.tpmFormGroup?.get('verifyBankAccountNumber'),
                        'Account numbers do not match.'
                    )
                ]);
            this.tpmFormGroup
                .get('verifyBankAccountNumber')
                .setValidators([
                    Validators.required,
                    this._stringI18nValidator.matchValueConfirm(
                        () => this.tpmFormGroup?.get('bankAccountNumber'),
                        'Account numbers do not match.'
                    )
                ]);
            this.tpmFormGroup
                .get('achRoutingNumber')
                .setValidators([this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)]);
            this.tpmFormGroup
                .get('wireRoutingNumber')
                .setValidators([this._stringI18nValidator.exactLength(ROUTING_NUMBER_LENGTH)]);
            this.tpmFormGroup
                .get('bankName')
                .setValidators([Validators.required, LsqValidators.maxLength(255)]);
        } else {
            this.tpmFormGroup.get('bankAccountName').setValidators([]);
            this.tpmFormGroup.get('bankAccountNumber').setValidators([]);
            this.tpmFormGroup.get('verifyBankAccountNumber').setValidators([]);
            this.tpmFormGroup.get('achRoutingNumber').setValidators([]);
            this.tpmFormGroup.get('wireRoutingNumber').setValidators([]);
            this.tpmFormGroup.get('bankName').setValidators([]);
        }

        this.tpmFormGroup.get('bankAccountName').updateValueAndValidity({ emitEvent: false });
        this.tpmFormGroup.get('bankAccountNumber').updateValueAndValidity({ emitEvent: false });
        this.tpmFormGroup
            .get('verifyBankAccountNumber')
            .updateValueAndValidity({ emitEvent: false });
        this.tpmFormGroup.get('achRoutingNumber').updateValueAndValidity({ emitEvent: false });
        this.tpmFormGroup.get('wireRoutingNumber').updateValueAndValidity({ emitEvent: false });
        this.tpmFormGroup.get('bankName').updateValueAndValidity({ emitEvent: false });
    }

    private _calculateCreditLimitFormGroupChanges(
        creditLimitFormGroupChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (
            +(creditLimitFormGroupChanges.creditLimit * 100) !==
            +this._cachedBuyerConfigurationResponse.buyerCreditLimitCents
        ) {
            changes.push({
                label: 'Credit Limit',
                newData: this._currencyPipe.transform(creditLimitFormGroupChanges.creditLimit),
                oldData:
                    this._cachedBuyerConfigurationResponse.buyerCreditLimitCents !== null
                        ? this._currencyPipe.transform(
                              this._cachedBuyerConfigurationResponse.buyerCreditLimitCents / 100
                          )
                        : '—'
            });
        }

        return changes;
    }

    private _calculateDefaultFlatFeeChanges(
        defaultFlatFeeFormChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (this._cachedBuyerConfigurationResponse.buyerDefaultFlatFeeRatePercent === null) {
            if (defaultFlatFeeFormChanges.defaultFlatFeeRate) {
                changes.push({
                    label: 'Default Flat Fee Rate',
                    oldData: '—',
                    newData: defaultFlatFeeFormChanges.defaultFlatFeeRate + ' %'
                });
            }
        } else if (
            defaultFlatFeeFormChanges.defaultFlatFeeRate !==
            formatBpsInput(this._cachedBuyerConfigurationResponse.buyerDefaultFlatFeeRatePercent)
        ) {
            changes.push({
                label: 'Default Flat Fee Rate',
                oldData:
                    formatBpsInput(
                        this._cachedBuyerConfigurationResponse.buyerDefaultFlatFeeRatePercent
                    ) + ' %',
                newData: !isEmpty(defaultFlatFeeFormChanges.defaultFlatFeeRate)
                    ? defaultFlatFeeFormChanges.defaultFlatFeeRate + ' %'
                    : '—'
            });
        }

        return changes;
    }

    private _calculateBuyerPaidEarlyPaymentFeesChanges(
        buyerPaidEarlyPaymentFeesChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (this._cachedBuyerConfigurationResponse.buyerPaidFeePercentage === null) {
            if (buyerPaidEarlyPaymentFeesChanges.buyerPaidFeePercentage) {
                changes.push({
                    label: 'Buyer Paid Fee Percentage',
                    oldData: '—',
                    newData: buyerPaidEarlyPaymentFeesChanges.buyerPaidFeePercentage + ' %'
                });
            }
        } else if (
            buyerPaidEarlyPaymentFeesChanges.buyerPaidFeePercentage !==
            this._cachedBuyerConfigurationResponse.buyerPaidFeePercentage
        ) {
            changes.push({
                label: 'Buyer Paid Fee Percentage',
                oldData: this._cachedBuyerConfigurationResponse.buyerPaidFeePercentage + ' %',
                newData: buyerPaidEarlyPaymentFeesChanges.buyerPaidFeePercentage
                    ? buyerPaidEarlyPaymentFeesChanges.buyerPaidFeePercentage + ' %'
                    : '—'
            });
        }

        return changes;
    }

    private _toggleBuyerPaidEarlyPaymentFeesVisibilityChanges(
        buyerPaidEarlyPaymentFeesVisibilityChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];
        //Outer enable toggle was off and is now on
        if (
            (this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible === null ||
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible === null) &&
            buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityEnabled
        ) {
            changes.push({
                label: 'Buyer Paid Fee Columns',
                oldData: '—',
                newData:
                    buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityActive.toString()
            });
            //Outer enable toggle was on and is still on with a value change to fee
        } else if (
            (this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible !== null &&
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible) !== null &&
            buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityEnabled &&
            buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityActive !==
                (this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible &&
                    this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible)
        ) {
            changes.push({
                label: 'Buyer Paid Fee Columns',
                oldData: (
                    this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible &&
                    this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible
                ).toString(),
                newData:
                    buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityActive.toString()
            });
            //The outer enable toggle was on and is now off
        } else if (
            this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible !== null &&
            this._cachedBuyerConfigurationResponse.buyerPaidTransactionFeeVisible !== null &&
            !buyerPaidEarlyPaymentFeesVisibilityChanges.buyerPaidEarlyPaymentFeeVisibilityEnabled
        ) {
            changes.push({
                label: 'Buyer Paid Fee Columns',
                oldData:
                    this._cachedBuyerConfigurationResponse.buyerPaidFeePercentageVisible.toString(),
                newData: '—'
            });
        }

        return changes;
    }

    private _toggleBuyerPaidEarlyPaymentTransferFeesChanges(
        buyerPaidEarlyPaymentTransferFeesChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];
        //Outer enable toggle was off and is now on
        if (
            this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee === null &&
            buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesEnabled
        ) {
            changes.push({
                label: 'Buyer Paid Transfer Fee',
                oldData: '—',
                newData:
                    buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesActive.toString()
            });
            //Outer enable toggle was on and is still on with a value change to fee
        } else if (
            this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee !== null &&
            buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesEnabled &&
            buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesActive !==
                this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee
        ) {
            changes.push({
                label: 'Buyer Paid Transfer Fee',
                oldData: this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee.toString(),
                newData:
                    buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesActive.toString()
            });
            //The outer enable toggle was on and is now off
        } else if (
            this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee !== null &&
            !buyerPaidEarlyPaymentTransferFeesChanges.buyerPaidEarlyPaymentTransferFeesEnabled
        ) {
            changes.push({
                label: 'Buyer Paid Transfer Fee',
                oldData: this._cachedBuyerConfigurationResponse.buyerPaidTransactionFee.toString(),
                newData: '—'
            });
        }

        return changes;
    }

    private _calculateMaxDaysEarlyChanges(
        maxDaysEarlyFormChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        this._toggleMaxDaysPaidEarly();

        if (
            !maxDaysEarlyFormChanges.maxDaysEarlyEnabled &&
            this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly === null
        ) {
            return [];
        } else if (
            !maxDaysEarlyFormChanges.maxDaysEarlyEnabled &&
            this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly !== null
        ) {
            changes.push({
                label: 'Maximum Days Paid Early',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly} Days`,
                newData: '—'
            });
        } else if (
            maxDaysEarlyFormChanges.maxDaysEarlyEnabled &&
            this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly === null
        ) {
            changes.push({
                label: 'Maximum Days Paid Early',
                oldData: '—',
                newData: maxDaysEarlyFormChanges.maxDaysEarlyDays + ' Days'
            });
        } else if (
            maxDaysEarlyFormChanges.maxDaysEarlyDays !==
            this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly
        ) {
            changes.push({
                label: 'Maximum Days Paid Early',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerInvoiceMaximumDaysPaidEarly} Days`,
                newData: `${maxDaysEarlyFormChanges.maxDaysEarlyDays}  Days`
            });
        }
        return changes;
    }

    private _calculateGracePeriodChanges(
        gracePeriodFormChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (
            !gracePeriodFormChanges.gracePeriodEnabled &&
            this._cachedBuyerConfigurationResponse.buyerGracePeriodDays === null
        ) {
            return [];
        } else if (
            !gracePeriodFormChanges.gracePeriodEnabled &&
            this._cachedBuyerConfigurationResponse.buyerGracePeriodDays !== null
        ) {
            changes.push({
                label: 'Grace Period',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerGracePeriodDays} Days`,
                newData: '—'
            });
        } else if (
            gracePeriodFormChanges.gracePeriodEnabled &&
            this._cachedBuyerConfigurationResponse.buyerGracePeriodDays === null
        ) {
            changes.push({
                label: 'Grace Period',
                oldData: '—',
                newData: gracePeriodFormChanges.gracePeriodDays + ' Days'
            });
        } else if (
            gracePeriodFormChanges.gracePeriodDays !==
            this._cachedBuyerConfigurationResponse.buyerGracePeriodDays
        ) {
            changes.push({
                label: 'Grace Period',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerGracePeriodDays} Days`,
                newData: `${gracePeriodFormChanges.gracePeriodDays}  Days`
            });
        }

        return changes;
    }

    private _calculateIngestAllInvoicesChanges(
        ingestAllInvoicesChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (!ingestAllInvoicesChanges.ingestAllInvoicesEnabled) {
            changes.push({
                label: 'Ingest All Invoices',
                oldData: 'Enabled',
                newData: 'Disabled'
            });
        } else if (ingestAllInvoicesChanges.ingestAllInvoicesEnabled) {
            changes.push({
                label: 'Ingest all invoices',
                oldData: 'Disabled',
                newData: 'Enabled'
            });
        }

        return changes;
    }

    private _calculateInforNexusEnabledChanges(
        inforNexusEnabledChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const configResponse = this
            ._cachedBuyerConfigurationResponse as GetBuyerConfigurationResponse;
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (!inforNexusEnabledChanges.inforNexusEnabled) {
            changes.push({
                label: 'InforNexus',
                oldData: 'Enabled',
                newData: 'Disabled'
            });
        } else if (inforNexusEnabledChanges.inforNexusEnabled) {
            changes.push({
                label: 'InforNexus',
                oldData: 'Disabled',
                newData: 'Enabled'
            });
        }

        return changes;
    }

    private _calculatePastDueFeeChanges(
        pastDueFeeFormChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (
            !pastDueFeeFormChanges.pastDueFeeEnabled &&
            this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent === null
        ) {
            return [];
        } else if (
            !pastDueFeeFormChanges.pastDueFeeEnabled &&
            this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent !== null
        ) {
            changes.push({
                label: 'Past Due Fee Rate',
                oldData:
                    formatBpsInput(this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent) +
                    ' %',
                newData: '—'
            });
        } else if (
            pastDueFeeFormChanges.pastDueFeeEnabled &&
            this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent === null
        ) {
            changes.push({
                label: 'Past Due Fee Rate',
                oldData: '—',
                newData: pastDueFeeFormChanges.pastDueFeeRate + ' %'
            });
        } else if (
            pastDueFeeFormChanges.pastDueFeeRate !==
            formatBpsInput(this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent)
        ) {
            changes.push({
                label: 'Past Due Fee Rate',
                oldData:
                    formatBpsInput(this._cachedBuyerConfigurationResponse.buyerPastDueFeePercent) +
                    ' %',
                newData: pastDueFeeFormChanges.pastDueFeeRate + ' %'
            });
        }

        return changes;
    }

    private _calculateRevenueShareConfigurationChanges(
        revenueShareFormGroupChanges: any,
        isUpdatingRevenueShareBankAccount
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (
            !revenueShareFormGroupChanges.enableRevenueShare &&
            this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent === null
        ) {
            return [];
        }

        if (
            !revenueShareFormGroupChanges.enableRevenueShare &&
            this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent !== null
        ) {
            changes.push({
                label: 'Revenue Share Configuration',
                oldData: 'Configured',
                newData: 'Deleted'
            });

            if (!!this._cachedBuyerConfigurationResponse.bankAccountName) {
                changes.push({
                    label: 'Revenue Share Bank Account',
                    oldData: 'Configured',
                    newData: 'Deleted'
                });
            }

            return changes;
        }

        if (
            isUpdatingRevenueShareBankAccount &&
            revenueShareFormGroupChanges.enableRevenueShare &&
            revenueShareFormGroupChanges.bankAccountName !==
                this._cachedBuyerConfigurationResponse.bankAccountName
        ) {
            changes.push({
                label: 'Revenue Share Bank Account Name',
                oldData: this._cachedBuyerConfigurationResponse.bankAccountName,
                newData: revenueShareFormGroupChanges.bankAccountName
            });
            changes.push({
                label: 'Revenue Share Bank Account Bank Name',
                oldData: this._cachedBuyerConfigurationResponse.bankAccountBankName,
                newData: revenueShareFormGroupChanges.bankName
            });
            changes.push({
                label: 'Revenue Share Bank Account Number',
                oldData: this._cachedBuyerConfigurationResponse.bankAccountNumberLastFour
                    ? `*****${this._cachedBuyerConfigurationResponse.bankAccountNumberLastFour}`
                    : null,
                newData: revenueShareFormGroupChanges.bankAccountNumber
            });
            changes.push({
                label: 'Revenue Share Bank Account Routing Number',
                oldData: this._cachedBuyerConfigurationResponse.bankAccountRoutingNumberLastFour
                    ? `*****${this._cachedBuyerConfigurationResponse.bankAccountRoutingNumberLastFour}`
                    : null,
                newData: revenueShareFormGroupChanges.routingNumber
            });
        }

        if (
            revenueShareFormGroupChanges.platformRevenueShare !==
            formatBpsInput(this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent)
        ) {
            changes.push({
                label: 'Platform Revenue Share',
                oldData: this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent
                    ? `${formatBpsInput(
                          this._cachedBuyerConfigurationResponse.buyerPlatformRevenueSharePercent
                      )} %`
                    : null,
                newData: `${revenueShareFormGroupChanges.platformRevenueShare} %`
            });
        }

        if (
            revenueShareFormGroupChanges.lsqRevenueShare !==
            formatBpsInput(this._cachedBuyerConfigurationResponse.buyerLsqRevenueSharePercent)
        ) {
            changes.push({
                label: 'LSQ Revenue Share',
                oldData: this._cachedBuyerConfigurationResponse.buyerLsqRevenueSharePercent
                    ? `${formatBpsInput(
                          this._cachedBuyerConfigurationResponse.buyerLsqRevenueSharePercent
                      )} %`
                    : null,
                newData: `${revenueShareFormGroupChanges.lsqRevenueShare} %`
            });
        }

        if (
            revenueShareFormGroupChanges.primaryBuyerRevenueShare !==
            formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerPrimaryBuyerRevenueSharePercent
            )
        ) {
            changes.push({
                label: 'Primary Buyer Revenue Share',
                oldData: this._cachedBuyerConfigurationResponse.buyerPrimaryBuyerRevenueSharePercent
                    ? `${formatBpsInput(
                          this._cachedBuyerConfigurationResponse
                              .buyerPrimaryBuyerRevenueSharePercent
                      )} %`
                    : null,
                newData: `${revenueShareFormGroupChanges.primaryBuyerRevenueShare} %`
            });
        }

        if (
            revenueShareFormGroupChanges.secondaryBuyerRevenueShare !==
            formatBpsInput(
                this._cachedBuyerConfigurationResponse.buyerSecondaryBuyerRevenueSharePercent
            )
        ) {
            changes.push({
                label: 'Secondary Buyer Revenue Share',
                oldData: this._cachedBuyerConfigurationResponse
                    .buyerSecondaryBuyerRevenueSharePercent
                    ? `${formatBpsInput(
                          this._cachedBuyerConfigurationResponse
                              .buyerSecondaryBuyerRevenueSharePercent
                      )} %`
                    : null,
                newData: `${revenueShareFormGroupChanges.secondaryBuyerRevenueShare} %`
            });
        }

        if (
            revenueShareFormGroupChanges.primaryDueDateThreshold !==
            this._cachedBuyerConfigurationResponse.buyerPrimaryDueDateThreshold
        ) {
            changes.push({
                label: 'Primary Due Date Threshold',
                oldData: this._cachedBuyerConfigurationResponse.buyerPrimaryDueDateThreshold,
                newData: revenueShareFormGroupChanges.primaryDueDateThreshold
            });
        }

        if (
            revenueShareFormGroupChanges.secondaryDueDateThreshold !==
            this._cachedBuyerConfigurationResponse.buyerSecondaryDueDateThreshold
        ) {
            changes.push({
                label: 'Secondary Due Date Threshold',
                oldData: this._cachedBuyerConfigurationResponse.buyerSecondaryDueDateThreshold,
                newData: revenueShareFormGroupChanges.secondaryDueDateThreshold
            });
        }

        return changes;
    }

    private _togglePayLaterSection(emitEvent: boolean = true) {
        if (this.tpmFormGroup.get('tpmEnabled').value) {
            this.payLaterFormGroup.get('payLaterEnabled').enable({ emitEvent });
        } else {
            this.payLaterFormGroup.get('payLaterEnabled').setValue(false, { emitEvent });
            this.payLaterFormGroup.get('payLaterEnabled').disable({ emitEvent });
        }
    }

    private _calculateTpmConfigurationChanges(
        tpmFormGroupChanges: any,
        isUpdatingTpmBankAccount
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        this._togglePayLaterSection();

        if (
            !tpmFormGroupChanges.tpmEnabled &&
            this._cachedBuyerConfigurationResponse.fboConfiguration?.accountName === null
        ) {
            return [];
        }

        if (
            !tpmFormGroupChanges.tpmEnabled &&
            !!this._cachedBuyerConfigurationResponse.fboConfiguration?.accountName
        ) {
            changes.push({
                label: 'Total Payment Management',
                oldData: 'Configured',
                newData: 'Deleted'
            });

            return changes;
        }

        if (isUpdatingTpmBankAccount && tpmFormGroupChanges.tpmEnabled) {
            changes.push({
                label: 'FBO Bank Account Name',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.accountName,
                newData: tpmFormGroupChanges.bankAccountName
            });
            changes.push({
                label: 'FBO Bank Account Bank Name',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.bankName,
                newData: tpmFormGroupChanges.bankName
            });
            changes.push({
                label: 'FBO Bank Account Number',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration
                    ?.accountNumberLastFour
                    ? `*****${this._cachedBuyerConfigurationResponse.fboConfiguration?.accountNumberLastFour}`
                    : null,
                newData: tpmFormGroupChanges.bankAccountNumber
            });
            changes.push({
                label: 'FBO Bank Account ACH Routing Number',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration
                    ?.achRoutingNumberLastFour
                    ? `*****${this._cachedBuyerConfigurationResponse.fboConfiguration?.achRoutingNumberLastFour}`
                    : null,
                newData: tpmFormGroupChanges.achRoutingNumber
            });
            changes.push({
                label: 'FBO Bank Account Wire Routing Number',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration
                    ?.wireRoutingNumberLastFour
                    ? `*****${this._cachedBuyerConfigurationResponse.fboConfiguration?.wireRoutingNumberLastFour}`
                    : null,
                newData: tpmFormGroupChanges.wireRoutingNumber
            });
        }

        if (
            tpmFormGroupChanges.companyId !==
            this._cachedBuyerConfigurationResponse.fboConfiguration?.bankCompanyId
        ) {
            changes.push({
                label: 'FBO Company Id',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.bankCompanyId,
                newData: tpmFormGroupChanges.companyId
            });
        }

        if (
            tpmFormGroupChanges.senderId !==
            this._cachedBuyerConfigurationResponse.fboConfiguration?.bankSenderId
        ) {
            changes.push({
                label: 'FBO Sender Id',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.bankSenderId,
                newData: tpmFormGroupChanges.senderId
            });
        }

        if (
            tpmFormGroupChanges.fboAccountType !==
            this._cachedBuyerConfigurationResponse.fboConfiguration?.fboAccountType
        ) {
            changes.push({
                label: 'FBO Account Type',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.fboAccountType,
                newData: tpmFormGroupChanges.fboAccountType
            });
        }
        if (
            tpmFormGroupChanges.outputFolderId !==
            this._cachedBuyerConfigurationResponse.fboConfiguration?.outputFolderId
        ) {
            changes.push({
                label: 'FBO Payment Instruction Folder Id',
                oldData: this._cachedBuyerConfigurationResponse.fboConfiguration?.outputFolderId,
                newData: tpmFormGroupChanges.outputFolderId
            });
        }
        return changes;
    }

    private _calculateDecayingFeeChanges(
        decayingFeeFormGroupChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        if (this._cachedBuyerConfigurationResponse.buyerDecayingFeeRatePercent === null) {
            if (decayingFeeFormGroupChanges.decayingFeeRate) {
                changes.push({
                    label: 'Decaying Fee Rate',
                    oldData: '—',
                    newData: decayingFeeFormGroupChanges.decayingFeeRate + ' %'
                });
            }
        } else if (
            decayingFeeFormGroupChanges.decayingFeeRate !==
            formatHbpsInput(this._cachedBuyerConfigurationResponse.buyerDecayingFeeRatePercent)
        ) {
            changes.push({
                label: 'Decaying Fee Rate',
                oldData:
                    formatHbpsInput(
                        this._cachedBuyerConfigurationResponse.buyerDecayingFeeRatePercent
                    ) + ' %',
                newData: !isEmpty(decayingFeeFormGroupChanges.decayingFeeRate)
                    ? decayingFeeFormGroupChanges.decayingFeeRate + ' %'
                    : '—'
            });
        }

        return changes;
    }

    private _calculateIneligibleDaysBeforeTermsChanges(
        ineligibleDaysBeforeTermsFormGroupChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        this._toggleIneligibleDayTerms();

        if (
            !ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTermsEnabled &&
            this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms === null
        ) {
            return [];
        } else if (
            !ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTermsEnabled &&
            this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms !== null
        ) {
            changes.push({
                label: 'Ineligible Days Before Terms',
                oldData: this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms,
                newData: '—'
            });
        } else if (
            ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTermsEnabled &&
            this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms === null
        ) {
            changes.push({
                label: 'Ineligible Days Before Terms',
                oldData: '—',
                newData: ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTerms
            });
        } else if (
            ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTerms !==
            this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms
        ) {
            changes.push({
                label: 'Ineligible Days Before Terms',
                oldData: this._cachedBuyerConfigurationResponse.buyerIneligibleDaysBeforeTerms,
                newData: ineligibleDaysBeforeTermsFormGroupChanges.ineligibleDaysBeforeTerms
            });
        }

        return changes;
    }

    private _calculatePayLaterChanges(formChanges: any): AdminManageBuyerPayLaterChanges[] {
        const configResponse = (
            this._cachedBuyerConfigurationResponse as GetBuyerConfigurationResponse
        ).payLaterConfiguration;
        let changes: AdminManageBuyerPayLaterChanges[] = [];

        if (configResponse) {
            const facilityLimit = configResponse.facilityLimitCents
                ? configResponse.facilityLimitCents / 100
                : null;
            const facilityLimitCurrencyCode = configResponse.facilityLimitCurrencyCode ?? '—';
            const daysIncrement = configResponse.daysIncrement ?? '—';
            const feeDailyPercent = configResponse.feeDailyPercent ?? '—';
            const lateFeeDailyPercent = configResponse.lateFeeDailyPercent ?? '—';
            const maxExtendableInvoiceAgeDays = configResponse.maxExtendableInvoiceAgeDays ?? '—';

            if (formChanges.payLaterEnabled && formChanges.facilityLimit != facilityLimit) {
                changes.push({
                    label: 'FlexTerm Credit Limit',
                    oldData: facilityLimit ? this._currencyPipe.transform(facilityLimit) : '—',
                    newData: this._currencyPipe.transform(formChanges.facilityLimit)
                });
            }
            if (
                formChanges.payLaterEnabled &&
                formChanges.facilityLimitCurrencyCode !== facilityLimitCurrencyCode
            ) {
                changes.push({
                    label: 'FlexTerm Currency',
                    oldData: facilityLimitCurrencyCode,
                    newData: formChanges.facilityLimitCurrencyCode
                });
            }
            if (formChanges.payLaterEnabled && formChanges.daysIncrement != daysIncrement) {
                changes.push({
                    label: 'FlexTerm Days Extension',
                    oldData: daysIncrement,
                    newData: formChanges.daysIncrement
                });
            }
            if (
                formChanges.maxExtendableInvoiceAgeDays &&
                formChanges.maxExtendableInvoiceAgeDays != maxExtendableInvoiceAgeDays
            ) {
                changes.push({
                    label: 'FlexTerm Max Extended Terms',
                    oldData: maxExtendableInvoiceAgeDays,
                    newData: formChanges.maxExtendableInvoiceAgeDays
                });
            }
            if (formChanges.payLaterEnabled && formChanges.feeDailyPercent != feeDailyPercent) {
                changes.push({
                    label: 'FlexTerm Primary Fee Rate',
                    oldData: `${feeDailyPercent} %`,
                    newData: `${formatHbpsInput(formChanges.feeDailyPercent)} %`
                });
            }
            if (
                formChanges.payLaterEnabled &&
                formChanges.lateFeeDailyPercent != lateFeeDailyPercent
            ) {
                changes.push({
                    label: 'FlexTerm Secondary Fee Rate',
                    oldData: `${lateFeeDailyPercent} %`,
                    newData: `${formatHbpsInput(formChanges.lateFeeDailyPercent)} %`
                });
            }
            if (!formChanges.payLaterEnabled) {
                return [
                    {
                        label: 'FlexTerm',
                        oldData: 'Configured',
                        newData: 'Deleted'
                    }
                ];
            }
        } else {
            if (!isEmpty(formChanges.facilityLimit)) {
                changes.push({
                    label: 'FlexTerm Credit Limit',
                    oldData: '—',
                    newData: this._currencyPipe.transform(formChanges.facilityLimit)
                });
            }
            if (!isEmpty(formChanges.facilityLimitCurrencyCode)) {
                changes.push({
                    label: 'FlexTerm Currency',
                    oldData: '—',
                    newData: formChanges.facilityLimitCurrencyCode
                });
            }
            if (!isEmpty(formChanges.daysIncrement)) {
                changes.push({
                    label: 'FlexTerm Days Extension',
                    oldData: '—',
                    newData: formChanges.daysIncrement
                });
            }
            if (!isEmpty(formChanges.maxExtendableInvoiceAgeDays)) {
                changes.push({
                    label: 'FlexTerm Max Extended Terms',
                    oldData: '—',
                    newData: formChanges.maxExtendableInvoiceAgeDays
                });
            }
            if (!isEmpty(formChanges.feeDailyPercent)) {
                changes.push({
                    label: 'FlexTerm Primary Fee Rate',
                    oldData: '—',
                    newData: `${formatHbpsInput(formChanges.feeDailyPercent)} %`
                });
            }
            if (!isEmpty(formChanges.lateFeeDailyPercent)) {
                changes.push({
                    label: 'FlexTerm Secondary Fee Rate',
                    oldData: '—',
                    newData: `${formatHbpsInput(formChanges.lateFeeDailyPercent)} %`
                });
            }
        }

        return changes;
    }

    private _setPayLaterValidators() {
        this.payLaterFormGroup
            .get('facilityLimit')
            .setValidators([Validators.required, LsqValidators.minNumber(0)]);
        this.payLaterFormGroup
            .get('facilityLimitCurrencyCode')
            .setValidators([Validators.required]);
        this.payLaterFormGroup
            .get('daysIncrement')
            .setValidators([
                Validators.required,
                LsqValidators.minNumber(0),
                LsqValidators.maxNumber(60)
            ]);
        this.payLaterFormGroup
            .get('maxExtendableInvoiceAgeDays')
            .setValidators([
                Validators.required,
                LsqValidators.minNumber(0),
                LsqValidators.maxNumber(150)
            ]);
        this.payLaterFormGroup
            .get('feeDailyPercent')
            .setValidators([
                Validators.required,
                LsqValidators.minNumber(0),
                LsqValidators.lessThanNumber(100)
            ]);
        this.payLaterFormGroup
            .get('lateFeeDailyPercent')
            .setValidators([
                Validators.required,
                LsqValidators.minNumber(0),
                LsqValidators.lessThanNumber(100)
            ]);
    }

    private _calculateAdjustableDaysChanges(
        adjustable120DaysGroupChanges: any
    ): AdminManageBuyerInformationChanges[] {
        const changes: AdminManageBuyerInformationChanges[] = [];

        this._toggleAdjustable120DaysRuleSection();
        if (
            !adjustable120DaysGroupChanges.adjustable120DayEnabled &&
            this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays === null
        ) {
            return [];
        } else if (
            !adjustable120DaysGroupChanges.adjustable120DayEnabled &&
            this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays !== null
        ) {
            changes.push({
                label: 'Ajustable 120 Days Rule',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays} Days`,
                newData: '—'
            });
        } else if (
            adjustable120DaysGroupChanges.adjustable120DayEnabled &&
            this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays === null
        ) {
            changes.push({
                label: 'Adjustable 120 Days Rule',
                oldData: '—',
                newData: adjustable120DaysGroupChanges.adjustable120DayActive + ' Days'
            });
        } else if (
            adjustable120DaysGroupChanges.adjustable120DayActive !==
            this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays
        ) {
            changes.push({
                label: 'Adjustable 120 Days Rule',
                oldData: `${this._cachedBuyerConfigurationResponse.buyerMaxFundableInvoiceAgeDays} Days`,
                newData: `${adjustable120DaysGroupChanges.adjustable120DayActive}  Days`
            });
        }
        return changes;
    }

    private _setupChangesStreams(): void {
        // Decided to split up these changes into separate streams because initially the changes modal was going to be split
        // into sections the same way the page is, but we decided to punt on that for now. Keeping the streams
        // like this in case we come back to it.
        this.creditLimitChanges$ = combineLatest([
            this.formsInitialized$,
            this.creditLimitFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, creditLimitFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateCreditLimitFormGroupChanges(creditLimitFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.revenueShareConfigurationChanges$ = combineLatest([
            this.formsInitialized$,
            this.revenueShareFormGroup.valueChanges,
            this.isUpdatingRevenueShareBankAccount$
        ]).pipe(
            map(
                ([
                    formsInitialized,
                    revenueShareFormGroupChanges,
                    isUpdatingRevenueShareBankAccount
                ]) => {
                    if (!formsInitialized) {
                        return [];
                    }

                    return this._calculateRevenueShareConfigurationChanges(
                        revenueShareFormGroupChanges,
                        isUpdatingRevenueShareBankAccount
                    );
                }
            ),
            startWith([]),
            shareReplay(1)
        );

        this.tpmConfigurationChanges$ = combineLatest([
            this.formsInitialized$,
            this.tpmFormGroup.valueChanges,
            this.isUpdatingTpmBankAccount$
        ]).pipe(
            map(([formsInitialized, tpmFormGroupChanges, isUpdatingTpmBankAccount]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateTpmConfigurationChanges(
                    tpmFormGroupChanges,
                    isUpdatingTpmBankAccount
                );
            }),
            startWith([]),
            shareReplay(1)
        );

        this.defaultFlatFeeChanges$ = combineLatest([
            this.formsInitialized$,
            this.defaultFlatFeeFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, defaultFlatFeeFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateDefaultFlatFeeChanges(defaultFlatFeeFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.maxDaysEarlyChanges$ = combineLatest([
            this.formsInitialized$,
            this.maxDaysEarlyFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, maxDaysEarlyFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateMaxDaysEarlyChanges(maxDaysEarlyFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.gracePeriodChanges$ = combineLatest([
            this.formsInitialized$,
            this.gracePeriodFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, gracePeriodFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateGracePeriodChanges(gracePeriodFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.ingestAllInvoicesChanges$ = combineLatest([
            this.formsInitialized$,
            this.ingestAllInvoicesFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, ingestAllFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateIngestAllInvoicesChanges(ingestAllFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.inforNexusEnabledChanges$ = combineLatest([
            this.formsInitialized$,
            this.inforNexusEnabledFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, inforNexusFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateInforNexusEnabledChanges(inforNexusFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.pastDueFeeChanges$ = combineLatest([
            this.formsInitialized$,
            this.pastDueFeeFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, pastDueFeeFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculatePastDueFeeChanges(pastDueFeeFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.buyerPaidEarlyPaymentFeesChanges$ = combineLatest([
            this.formsInitialized$,
            this.buyerPaidEarlyPaymentFeesFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, buyerPaidEarlyPaymentFeesChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateBuyerPaidEarlyPaymentFeesChanges(
                    buyerPaidEarlyPaymentFeesChanges
                );
            }),
            startWith([]),
            shareReplay(1)
        );

        this.buyerPaidEarlyPaymentFeesVisibilityChanges$ = combineLatest([
            this.formsInitialized$,
            this.buyerPaidEarlyPaymentFeeVisibilityFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, buyerPaidEarlyPaymentFeesVisibilityChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._toggleBuyerPaidEarlyPaymentFeesVisibilityChanges(
                    buyerPaidEarlyPaymentFeesVisibilityChanges
                );
            }),
            startWith([]),
            shareReplay(1)
        );

        this.buyerPaidEarlyPaymentTransferFeesChanges$ = combineLatest([
            this.formsInitialized$,
            this.buyerPaidEarlyPaymentTransferFeesFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, buyerPaidEarlyPaymentTransferFeesChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._toggleBuyerPaidEarlyPaymentTransferFeesChanges(
                    buyerPaidEarlyPaymentTransferFeesChanges
                );
            }),
            startWith([]),
            shareReplay(1)
        );

        this.decayingFeeChanges$ = combineLatest([
            this.formsInitialized$,
            this.decayingFeeFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, decayingFeeFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateDecayingFeeChanges(decayingFeeFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.ineligibleDaysBeforeTermsChanges$ = combineLatest([
            this.formsInitialized$,
            this.ineligibleDaysBeforeTermsFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, ineligibleDaysBeforeTermsFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }

                return this._calculateIneligibleDaysBeforeTermsChanges(
                    ineligibleDaysBeforeTermsFormGroupChanges
                );
            }),
            startWith([]),
            shareReplay(1)
        );

        this.payLaterChanges$ = combineLatest([
            this.formsInitialized$,
            this.payLaterFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, payLaterChanges]) => {
                if (!formsInitialized) {
                    return [];
                }
                return this._calculatePayLaterChanges(payLaterChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.adjustable120DaysChanges$ = combineLatest([
            this.formsInitialized$,
            this.adjustable120DayFormGroup.valueChanges
        ]).pipe(
            map(([formsInitialized, adjustable120DayFormGroupChanges]) => {
                if (!formsInitialized) {
                    return [];
                }
                return this._calculateAdjustableDaysChanges(adjustable120DayFormGroupChanges);
            }),
            startWith([]),
            shareReplay(1)
        );

        this.hasChanges$ = combineLatest([
            this.adjustable120DaysChanges$,
            this.ineligibleDaysBeforeTermsChanges$,
            this.creditLimitChanges$,
            this.defaultFlatFeeChanges$,
            this.maxDaysEarlyChanges$,
            this.gracePeriodChanges$,
            this.pastDueFeeChanges$,
            this.buyerPaidEarlyPaymentFeesChanges$,
            this.buyerPaidEarlyPaymentFeesVisibilityChanges$,
            this.buyerPaidEarlyPaymentTransferFeesChanges$,
            this.revenueShareConfigurationChanges$,
            this.tpmConfigurationChanges$,
            this.decayingFeeChanges$,
            this.payLaterChanges$,
            this.ingestAllInvoicesChanges$,
            this.inforNexusEnabledChanges$
        ]).pipe(
            map((allChanges) => allChanges.some((changes) => changes.length > 0)),
            startWith(false),
            shareReplay(1)
        );
    }
}
